import { usePermissions } from '@/hooks/usePermissions'
import type { PermissionEntityType } from '@/api/model'

/**
 * Declarative form of a permission check, shared by <Can> and
 * <RequirePermission>. Mirrors the three semantics of `usePermissions`:
 *  - neither `entityId` nor `any`  → general grant only
 *  - `entityId` set                → general OR that specific per-entity grant
 *  - `any`                         → general OR any per-entity grant
 */
export interface PermissionCheck {
  entityType: PermissionEntityType
  permission: string
  /** Per-entity id: passes if the general grant OR this specific id is granted. */
  entityId?: number
  /** Passes if the general grant OR *any* per-entity grant of this kind exists. */
  any?: boolean
}

type Checkers = ReturnType<typeof usePermissions>

function evaluate(check: PermissionCheck, c: Checkers): boolean {
  if (check.entityId != null) {
    return c.userHasEntityPermission(check.entityType, check.permission, check.entityId)
  }
  if (check.any) {
    return c.userHasAnyPermission(check.entityType, check.permission)
  }
  return c.userHasPermission(check.entityType, check.permission)
}

export function useHasAccess(check: PermissionCheck): boolean {
  return evaluate(check, usePermissions())
}

/** Passes if *any* of the checks passes (OR). For compound gates like /company. */
export function useHasAnyAccess(checks: PermissionCheck[]): boolean {
  const c = usePermissions()
  return checks.some((check) => evaluate(check, c))
}
